import { useEffect, useRef, useState } from 'react'
import { Button, Spinner } from 'react-bootstrap'
import Webcam from 'react-webcam'

import { useApprovePackageRequest } from '../../../../api/package-req'
import { uploadFile } from '../../../../services/files'

const base64ToBlob = base64Image => {
  const base64 = base64Image.split(',')[1]
  const arrayBuffer = Uint8Array.from(atob(base64), c => c.charCodeAt(0))
  const blob = new Blob([arrayBuffer], { type: 'image/jpeg' })
  return blob
}

export const PhotoRequestDetails = ({ item, onApprove, onBack }) => {
  // Get available cameras when component mounts
  const [image, setImage] = useState(null)
  const [imageBlob, setimageBlob] = useState(null)
  const webcamRef = useRef(null)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [availableCameras, setAvailableCameras] = useState([])
  const [selectedCamera, setSelectedCamera] = useState(null)
  const approvePackageRequestMutation = useApprovePackageRequest()
  const dropdownRef = useRef(null)
  const handleCameraChange = cameraId => {
    setSelectedCamera(cameraId)
    setIsDropdownOpen(false)
    // Reset image when changing camera
    setImage(null)
    setimageBlob(null)
  }

  useEffect(() => {
    const handleClickOutside = event => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const capturePhoto = () => {
    if (webcamRef.current) {
      const base64Image = webcamRef.current.getScreenshot()
      //for showing the image
      setImage(base64Image)
      const blob = base64ToBlob(base64Image)
      //for uploading the image
      setimageBlob(blob)
    }
  }

  const deletePhoto = () => {
    setImage(null)
    setimageBlob(null)
  }
  const savePhoto = async () => {
    if (!image) return

    try {
      //upload s3
      const s3result = await uploadFile(imageBlob, item.id + '_photo.jpg')
      // console.log('savePhoto - result.location:', s3result.Location)

      //approve package req
      const dataForApprove = {
        packageReqId: item.id,
        photoUrl: s3result.Location,
      }
      const res = await approvePackageRequestMutation.mutateAsync(dataForApprove)
      if (res.statusCode !== 200) {
        const message = res.msg || 'Error approving photo request'
        alert(message)
        return
      }

      //reset states
      setImage(null)
      setimageBlob(null)
      onApprove()
    } catch (error) {
      console.log('savePhoto - error:', error)
      alert('Error saving photo!', error)
    }
  }

  useEffect(() => {
    const getCameras = async () => {
      try {
        // First request camera permissions to get proper device information
        await navigator.mediaDevices.getUserMedia({ video: true })

        // Now enumerate devices - they should have proper deviceId and label
        let devices = await navigator.mediaDevices.enumerateDevices()
        const videoDevices = devices.filter(device => device.kind === 'videoinput')
        setAvailableCameras(videoDevices)

        if (videoDevices.length > 0) {
          // Try to find the back camera first (environment facing)
          const backCamera = videoDevices.find(
            camera =>
              camera.label.toLowerCase().includes('back') ||
              camera.label.toLowerCase().includes('rear') ||
              camera.label.toLowerCase().includes('environment') ||
              camera.facingMode === 'environment'
          )
          // If back camera found, use it; otherwise use the first available camera
          setSelectedCamera(backCamera ? backCamera.deviceId : videoDevices[0].deviceId)
        }
      } catch (error) {
        console.error('Error getting cameras:', error)
        // If permission denied, still try to enumerate devices
        try {
          let devices = await navigator.mediaDevices.enumerateDevices()
          const videoDevices = devices.filter(device => device.kind === 'videoinput')
          setAvailableCameras(videoDevices)
          if (videoDevices.length > 0) {
            setSelectedCamera(videoDevices[0].deviceId)
          }
        } catch (enumerateError) {
          console.error('Error enumerating devices:', enumerateError)
        }
      }
    }
    getCameras()
  }, [])

  if (approvePackageRequestMutation.isPending) {
    return (
      <div className='package-requests-cmp'>
        Loading... <br />
        <Spinner animation='border' variant='primary' />
      </div>
    )
  }

  return (
    <div className='req-form-cont'>
      <div className='d-flex justify-content-between align-items-center'>
        <h2>Take a Photo</h2>
        <Button variant='danger' size='sm' className='btn-icon ml-4' onClick={onBack}>
          X
        </Button>
      </div>
      <div className='camera-container'>
        {image ? (
          <div className='photo-preview'>
            <img src={image} alt='Captured' />
            <div>
              <Button variant='danger' onClick={deletePhoto}>
                Delete Picture
              </Button>
              <Button variant='success' onClick={savePhoto}>
                Save & Approve
              </Button>
            </div>
          </div>
        ) : (
          <div className='camera-preview'>
            {availableCameras.length > 1 && (
              <div className='camera-selector'>
                <div ref={dropdownRef} className='camera-dropdown'>
                  <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className='camera-dropdown-button'>
                    <span>
                      {selectedCamera
                        ? availableCameras.find(cam => cam.deviceId === selectedCamera)?.label ||
                          'Camera ' + (availableCameras.findIndex(cam => cam.deviceId === selectedCamera) + 1)
                        : 'Select Camera'}
                    </span>
                    <span className='camera-dropdown-arrow'>{isDropdownOpen ? '▲' : '▼'}</span>
                  </button>
                  {isDropdownOpen && (
                    <div className='camera-dropdown-menu'>
                      {availableCameras.map((camera, index) => (
                        <div
                          key={camera.deviceId}
                          onClick={() => handleCameraChange(camera.deviceId)}
                          className={`camera-dropdown-item ${selectedCamera === camera.deviceId ? 'active' : ''}`}
                        >
                          {camera.label || `Camera ${index + 1}`}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
            <Webcam
              ref={webcamRef}
              screenshotFormat='image/jpeg'
              width='100%'
              height='100%'
              videoConstraints={{
                deviceId: selectedCamera,
              }}
            />
            <Button variant='primary' onClick={capturePhoto}>
              Take Photo
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
